import {initFlatpickrRange} from "@core/component/date-picker/flatpickr-range.js";
import { Calendar as FullCalendar} from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import { getLocalDateString, getLocalTimeString} from '@core/dateUtils.js';
import interactionPlugin from '@fullcalendar/interaction';
import {parseISODate} from '@core/dateUtils.js'
import '@fullcalendar/web-component/global'
import * as bootstrap5 from "bootstrap";
import './calendar.css'
import { getCsrfHeaders } from '@core/csrfUtils.js';

// Activity Calendar Component
export default (activityId, actVon, actBis, isActivityInProgress) => ({
    async init() {
        initCalendar(this.$el,activityId, actVon || null, actBis || null, isActivityInProgress);
    }
});

function initCalendar(el,activityId, actVon, actBis, isActivityInProgress) {
    const shadowRoot = el.getRootNode();
    const calendarEl = el.querySelector('[data-calendar]');
    let calendar;

    // Valid range configuration if dates are provided
    const validRangeConfig = {};
    if (actVon) {
        validRangeConfig.start = actVon;
    }
    if (actBis) {
        validRangeConfig.end = actBis;
    }

// Calendar initialization
    calendar = new FullCalendar(calendarEl, {
        plugins: [dayGridPlugin, interactionPlugin],
        initialView: 'dayGridMonth',
        // Let rows size to their content instead of stretching to fill the height
        contentHeight: 'auto',
        locale: 'ch-fr',
        headerToolbar: {
            left: 'prev,next today',
            center: '',
            right: 'title'
        },
        buttonText: {
            today: getMessage('kibe.calendar.button.today'),
            month: getMessage('kibe.calendar.button.month'),
            week: getMessage('kibe.calendar.button.week'),
            day: getMessage('kibe.calendar.button.day')
        },
        firstDay : 1,
        editable: !isActivityInProgress,
        selectable: !isActivityInProgress,
        selectMirror: true,
        dayMaxEvents: true,
        weekends: false,
        validRange: Object.keys(validRangeConfig).length > 0 ? validRangeConfig : undefined,

        // Fetch events from the server
        events: function (info, successCallback, failureCallback) {
            const startDate = getLocalDateString(info.start);
            const endDate = getLocalDateString(info.end);

            fetch(window.NEXT_URL + `/activity-calendar/events?id=${activityId}&start=${startDate}&end=${endDate}`)
                .then(response => response.json())
                .then(data => {
                    // Transform DTO format to FullCalendar format
                    const events = data.map(event => {
                        const calEvent = {
                            id: event.id,
                            title: event.title,
                            start: `${event.date}T${event.startTime}`,
                            end: `${event.date}T${event.endTime}`,
                            allDay: event.allDay,
                            className: event.className || 'calendar-event-activity',
                            extendedProps: {
                                eventType: event.eventType
                            }
                        };

                        // If it's a special day, mark it as background
                        if (event.display) {
                            calEvent.display = 'background';
                            calEvent.editable = false;
                            calEvent.className = ['special-day-event', event.className];
                        }

                        return calEvent;
                    });
                    successCallback(events);
                })
                .catch(error => {
                    console.error(getMessage('kibe.activity.calendar.error.loading_events'), error);
                    failureCallback(error);
                });
        },

        eventContent: function(arg) {
            const start = arg.event.start;
            const end = arg.event.end;

            const startTime = start ? start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
            const endTime = end ? end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';

            let timeText;
            if (arg.event.extendedProps && arg.event.extendedProps.eventType === 'special-day') {
                timeText = arg.event.title;
            }
            else {
                timeText = `${startTime}${endTime ? ' - ' + endTime : ''}`;
            }

            // Horizontal centering
            return { html: `<div class="fc-event-centered">${timeText}</div>` };
        },

        // Create a new event
        select: function (info) {
            if (isActivityInProgress) {
                calendar.unselect();
                return;
            }

            const startDate = info.startStr;
            const endDate = new Date(info.end);
            endDate.setDate(endDate.getDate() - 1);
            const endDateStr = getLocalDateString(endDate);

            // Check if there is a special day in the selection
            const allEvents = calendar.getEvents();

            // const hasSpecialDay = allEvents.some(event => {
            //     if (event.extendedProps && event.extendedProps.eventType === 'special-day') {
            //         const eventDate = getLocalDateString(event.start);
            //         const selectionStart = new Date(startDate);
            //         const selectionEnd = new Date(endDateStr);
            //         const checkDate = new Date(eventDate);
            //         return checkDate >= selectionStart && checkDate <= selectionEnd;
            //     }
            //     return false;
            // });
            //
            // if (hasSpecialDay) {
            //     alert('Cannot create an event on a public holiday or during school holidays.');
            //     calendar.unselect();
            //     return;
            // }

            // Check if an activity event already exists on the last cell
            const existingActivityEvents = allEvents.filter(event => {
                const eventDate = getLocalDateString(event.start);
                return eventDate === startDate &&
                    event.extendedProps &&
                    event.extendedProps.eventType === 'activity';
            });

            // If an activity event exists on the last cell, edit it
            if (existingActivityEvents.length > 0) {
                openEventModal(existingActivityEvents[0], startDate, endDateStr);
            } else {
                // Otherwise, create a new event
                openEventModal(null, startDate, endDateStr);
            }
            calendar.unselect();
        },

        // Click on an existing event
        eventClick: function (info) {
            if (isActivityInProgress) {
                return;
            }

            // Ignore clicks on special days (background events)
            if (info.event.extendedProps && info.event.extendedProps.eventType === 'special-day') {
                return;
            }

            openEventModal(info.event);
        },

        // Drag & drop an event
        eventDrop: function (info) {
            updateEventDate(info.event);
        },

        // Resize an event
        eventResize: function (info) {
            updateEventDate(info.event);
        }
    });

    calendar.render();

// Global variable to store the Flatpickr instance
    let dateRangePicker = null;

    function getModalElement() {
        return el.querySelector('#eventModal');
    }

    function getModalInstance() {
        return bootstrap5.Modal.getInstance(getModalElement());
    }

// --- Modal management functions ---
    function openEventModal(event, defaultDateFrom, defaultDateTo) {
        const modal = getModalElement();
        const modalTitle = modal.querySelector('[data-modal-label]');
        const deleteBtn = modal.querySelector('[data-delete-btn]');
        const eventIdInput = modal.querySelector('[data-event-id]');
        const eventDateFromInput = modal.querySelector('[data-event-date-from]');
        const eventDateToInput = modal.querySelector('[data-event-date-to]');
        const eventDateRangeInput = modal.querySelector('[data-event-date-range]');
        const eventStartTimeInput = modal.querySelector('[data-event-start-time]');
        const eventEndTimeInput = modal.querySelector('[data-event-end-time]');
        const eventRecurrenceSelect = modal.querySelector('[data-event-recurrence]');

        deleteBtn.style.display = 'block';

        let startDate, endDate;

        if (event) {
            // Edit mode
            modalTitle.textContent = getMessage('kibe.activity.calendar.modal.title.edit');
            eventIdInput.value = event.id;
            const eventDate = event.startStr.split('T')[0];
            startDate = eventDate;
            endDate = defaultDateTo || eventDate;
            eventDateFromInput.value = eventDate;
            eventDateToInput.value = defaultDateTo || eventDate;

            const startTimeValue = event.start ? event.start.toTimeString().substring(0, 5) : '';
            const endTimeValue = event.end ? event.end.toTimeString().substring(0, 5) : '';
            eventStartTimeInput.value = startTimeValue;
            eventEndTimeInput.value = endTimeValue;
        } else {
            // Creation mode
            modalTitle.textContent = getMessage('kibe.activity.calendar.modal.title.add');
            eventIdInput.value = '';
            startDate = defaultDateFrom;
            endDate = defaultDateTo || defaultDateFrom;
            eventDateFromInput.value = defaultDateFrom;
            eventDateToInput.value = defaultDateTo || defaultDateFrom;
            eventStartTimeInput.value = '';
            eventEndTimeInput.value = '';
        }

        // Initialize recurrence based on the date range
        const daysDiff = Math.floor((new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24));
        eventRecurrenceSelect.value = daysDiff > 0 ? 'WEEKLY' : 'ONCE';

        // Initialize weekday checkboxes
        initializeWeekDayCheckboxes(startDate);
        updateWeekDaysState();

        var initRecurence = (selectedDates)  => {
            if (selectedDates.length === 2) {
                // Initialize recurrence based on the range
                const daysDiff = Math.floor((selectedDates[1] - selectedDates[0]) / (1000 * 60 * 60 * 24));
                eventRecurrenceSelect.value = daysDiff > 0 ? 'WEEKLY' : 'ONCE';
                updateWeekDaysState();
            }
        };

        // Destroy the previous instance if it exists.
        // In static mode, Flatpickr appends a .flatpickr-calendar to the parent on each
        // init; without destroying, instances and their click/focus listeners stack up,
        // causing a stale calendar to reopen when re-interacting with the modal.
        if (dateRangePicker) {
            dateRangePicker.destroy();
            dateRangePicker = null;
        }


        // Initialize Flatpickr in range mode with the reusable function
        dateRangePicker = initFlatpickrRange(eventDateRangeInput, {
            defaultDate: [startDate, endDate],
            fromInput: eventDateFromInput,
            toInput: eventDateToInput,
            extraConfig: {minDate: parseISODate(actVon), maxDate: parseISODate(actBis)},
            onReady: function (selectedDates) {
                initRecurence(selectedDates);
            },
            onChange: function (selectedDates) {
                initRecurence(selectedDates);
            }
        });

        // Reset error display
        clearTimeValidationError();
        validateTimes();

        //
        //const modalBs = new bootstrap5.Modal(modal);
        getModalInstance().show();
    }

    // No need for conditional handling as "from" and "to" dates are always displayed

    // Function to validate that the start time is less than the end time
    function validateTimes() {
        const modal = getModalElement();
        const startTime = modal.querySelector('[data-event-start-time]').value;
        const endTime = modal.querySelector('[data-event-end-time]').value;
        const saveBtn = modal.querySelector('[data-save-btn]');

        // If both fields are not filled, disable the button
        if (!startTime || !endTime) {
            clearTimeValidationError();
            saveBtn.disabled = true;
            return false;
        }

        // If both fields are filled, check that start < end
        if (startTime >= endTime) {
            displayTimeValidationError(getMessage('kibe.activity.calendar.error.time_order'));
            saveBtn.disabled = true;
            return false;
        } else {
            clearTimeValidationError();
            saveBtn.disabled = false;
            return true;
        }
    }

    // Function to display the error message
    function displayTimeValidationError(message) {
        const modal = getModalElement();
        const form = modal.querySelector('[data-event-form]');
        let errorDiv = form.querySelector('[data-time-validation-error]');
        if (!errorDiv) {
            errorDiv = document.createElement('div');
            errorDiv.setAttribute('data-time-validation-error', '');
            errorDiv.className = 'alert alert-danger alert-sm mt-2 mb-0';
            errorDiv.style.fontSize = '0.875rem';
            errorDiv.style.padding = '0.5rem';
            form.appendChild(errorDiv);
        }
        errorDiv.textContent = message;
        errorDiv.style.display = 'block';
    }

    // Function to clear the error message
    function clearTimeValidationError() {
        const modal = getModalElement();
        const errorDiv = modal.querySelector('[data-time-validation-error]');
        if (errorDiv) {
            errorDiv.style.display = 'none';
        }
        modal.querySelector('[data-save-btn]').disabled = false;
    }

    // Add event listeners for real-time validation
    const modal = getModalElement();
    modal.querySelector('[data-event-start-time]').addEventListener('change', validateTimes);
    modal.querySelector('[data-event-end-time]').addEventListener('change', validateTimes);

    // Add listener for recurrence change
    modal.querySelector('[data-event-recurrence]').addEventListener('change', updateWeekDaysState);

// Save an event
    modal.querySelector('[data-save-btn]').addEventListener('click', function () {
        // Validate times before saving
        if (!validateTimes()) {
            return;
        }

        const eventId = modal.querySelector('[data-event-id]').value;
        const dateFrom = modal.querySelector('[data-event-date-from]').value;
        const dateTo = modal.querySelector('[data-event-date-to]').value;
        const startTime = modal.querySelector('[data-event-start-time]').value;
        const endTime = modal.querySelector('[data-event-end-time]').value;
        const recurrence = modal.querySelector('[data-event-recurrence]').value;

        // Validation: the "to" date must be >= the "from" date
        if (new Date(dateTo) < new Date(dateFrom)) {
            alert(getMessage('kibe.activity.calendar.error.date_order'));
            return;
        }

        const eventData = {
            date: dateFrom,
            startTime: startTime,
            endTime: endTime,
            allDay: false,
            recurrenceType: recurrence,
            recurrenceUntilDate: dateTo,
            selectedWeekDays: getSelectedWeekDays()
        };

        if (eventId) {
            // Update with recurrence
            fetch(window.NEXT_URL + `/activity-calendar/event/regen/${eventId}?activityId=${activityId}`, {
                method: 'PUT',
                headers: {'Content-Type': 'application/json', ...getCsrfHeaders(shadowRoot)},
                body: JSON.stringify(eventData)
            })
                .then(response => {
                    if (!response.ok) {
                        throw new Error(getMessage('kibe.activity.calendar.error.update'));
                    }
                })
                .then(() => {
                    calendar.refetchEvents();
                    getModalInstance().hide();
                })
                .catch(error => {
                    console.error(getMessage('kibe.activity.calendar.error.update'), error);
                    alert(getMessage('kibe.activity.calendar.error.update_event'));
                });
        } else {
            // Creation
            fetch(window.NEXT_URL + `/activity-calendar/event/create?activityId=${activityId}`, {
                method: 'POST',
                headers: {'Content-Type': 'application/json', ...getCsrfHeaders(shadowRoot)},
                body: JSON.stringify(eventData)
            })
                .then(response => {
                    if (!response.ok) {
                        throw new Error(getMessage('kibe.activity.calendar.error.create'));
                    }
                    calendar.refetchEvents();
                    getModalInstance().hide();
                })
                .catch(error => {
                    console.error(getMessage('kibe.activity.calendar.error.create'), error);
                    alert(getMessage('kibe.activity.calendar.error.create_event'));
                });
        }
    });

    modal.querySelector('[data-cancel-btn]').addEventListener('click', function () {
        getModalInstance().hide();
    })

// Delete an event
    modal.querySelector('[data-delete-btn]').addEventListener('click', function () {
        const dateFrom = modal.querySelector('[data-event-date-from]').value;
        const dateTo = modal.querySelector('[data-event-date-to]').value;
        const recurrence = modal.querySelector('[data-event-recurrence]').value;
        const recurrenceSelect = modal.querySelector('[data-event-recurrence]');

        let message;
        if (recurrence === 'ONCE' && dateFrom === dateTo) {
            message = getMessage('kibe.activity.calendar.confirm.delete_single');
        } else {
            message = getMessage('kibe.activity.calendar.confirm.delete_multiple', [
                recurrenceSelect.options[recurrenceSelect.selectedIndex].text,
                dateFrom,
                dateTo
            ]);
        }

        if (confirm(message)) {
            const eventId = modal.querySelector('[data-event-id]').value;

            const deleteData = {
                date: dateFrom,
                recurrenceUntilDate: dateTo,
                recurrenceType: recurrence,
                selectedWeekDays: getSelectedWeekDays()
            };

            fetch(window.NEXT_URL +  `/activity-calendar/event/delete/${activityId}`, {
                method: 'DELETE',
                headers: {'Content-Type': 'application/json', ...getCsrfHeaders(shadowRoot)},
                body: JSON.stringify(deleteData)
            })
                .then(response => {
                    if (response.ok) {
                        calendar.refetchEvents();
                        getModalInstance().hide();
                    } else {
                        throw new Error(getMessage('kibe.activity.calendar.error.delete'));
                    }
                })
                .catch(error => {
                    console.error(getMessage('kibe.activity.calendar.error.delete'), error);
                    alert(getMessage('kibe.activity.calendar.error.delete_event'));
                });
        }
    });

    // Function to initialize checkboxes based on the start date's day
    function initializeWeekDayCheckboxes(dateStr) {
        const modal = getModalElement();
        const date = new Date(dateStr);
        const dayOfWeek = date.getDay(); // 0=Sunday, 1=Monday, ..., 6=Saturday
        const isoDayOfWeek = dayOfWeek === 0 ? 7 : dayOfWeek; // Convert to ISO (1=Monday, 7=Sunday)

        // Uncheck all boxes
        modal.querySelectorAll('[data-weekday-checkbox]').forEach(cb => cb.checked = false);

        // Check the box corresponding to the date's day
        const checkbox = modal.querySelector(`[data-weekday="${isoDayOfWeek}"]`);
        if (checkbox) {
            checkbox.checked = true;
        }
    }

    // Function to enable/disable checkboxes based on recurrence type
    function updateWeekDaysState() {
        const modal = getModalElement();
        const recurrence = modal.querySelector('[data-event-recurrence]').value;
        const checkboxes = modal.querySelectorAll('[data-weekday-checkbox]');
        const checkboxTools = modal.querySelectorAll('[data-weekday-checkbox-tool]');
        const container = modal.querySelector('[data-weekdays-container]');

        if (recurrence === 'WEEKLY' || recurrence === 'BIWEEKLY') {
            // Enable checkboxes
            checkboxes.forEach(cb => cb.disabled = false);
            checkboxTools.forEach(cb => cb.disabled = false);

            container.style.opacity = '1';
        } else {
            // Disable checkboxes
            checkboxes.forEach(cb => cb.disabled = true);
            checkboxTools.forEach(cb => cb.disabled = true);
            container.style.opacity = '0.5';
        }
    }

    // Function to get selected weekdays
    function getSelectedWeekDays() {
        const modal = getModalElement();
        const selectedDays = [];
        modal.querySelectorAll('[data-weekday-checkbox]:checked').forEach(cb => {
            selectedDays.push(parseInt(cb.value));
        });
        return selectedDays;
    }

// Function to update the date after drag & drop or resize
    function updateEventDate(event) {
        const date = getLocalDateString(event.start);
        const startTime = getLocalTimeString(event.start);
        const endTime = event.end ? getLocalTimeString(event.end) : startTime;

        const eventData = {
            date: date,
            startTime: startTime,
            endTime: endTime,
            allDay: event.allDay
        };

        fetch(window.NEXT_URL + `/activity-calendar/event/update/${event.id}`, {
            method: 'PUT',
            headers: {'Content-Type': 'application/json', ...getCsrfHeaders(shadowRoot)},
            body: JSON.stringify(eventData)
        })
            .then(response => {
                if (!response.ok) throw new Error(getMessage('kibe.activity.calendar.error.update'));
            })
            .catch(error => {
                console.error(getMessage('kibe.activity.calendar.error.update'), error);
                alert(getMessage('kibe.activity.calendar.error.update_event'));
                calendar.refetchEvents();
            });
    }
}